import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Film, Loader2, Play, Plus, Trash2, ArrowUp, ArrowDown, RefreshCw, X, ImageIcon, Camera } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";
import { ShowcaseViewer } from "@/components/ShowcaseStrip";
import { ThumbnailPicker } from "@/components/admin/ThumbnailPicker";

const MAX = 10;
const ORIGIN = API.replace(/\/api$/, "");

/**
 * Owner-only manager for the public homepage "The Standard" marquee. Upload video
 * files (+ optional thumbnail), label/creator, enable/disable, reorder, replace,
 * delete, preview. Whatever is enabled here is exactly what the public reel plays.
 */
export const ShowcaseManager = () => {
  const [items, setItems] = useState(null);
  const [max, setMax] = useState(MAX);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", creator: "", source: "" });
  const [videoFile, setVideoFile] = useState(null);
  const [thumbFile, setThumbFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", creator: "", source: "" });
  const [framePickFor, setFramePickFor] = useState(null);
  const videoRef = useRef(null);
  const thumbRef = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/showcase`, { headers: authHeaders() })
      .then((r) => { setItems(r.data.items); setMax(r.data.max || MAX); })
      .catch(() => toast.error("Could not load the showcase list."));
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!videoFile) return toast.error("Choose a video file first.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("video", videoFile);
      if (thumbFile) fd.append("thumb", thumbFile);
      fd.append("title", form.title.trim());
      fd.append("creator", form.creator.trim());
      fd.append("source", form.source.trim());
      await axios.post(`${API}/admin/showcase`, fd, { headers: authHeaders() });
      toast.success("Showcase video added.");
      setForm({ title: "", creator: "", source: "" });
      setVideoFile(null); setThumbFile(null); setAdding(false);
      if (videoRef.current) videoRef.current.value = "";
      if (thumbRef.current) thumbRef.current.value = "";
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed.");
    } finally { setBusy(false); }
  };

  const patch = async (id, fields, files = {}) => {
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(fields).forEach(([k, v]) => fd.append(k, String(v)));
      Object.entries(files).forEach(([k, f]) => f && fd.append(k, f));
      await axios.patch(`${API}/admin/showcase/${id}`, fd, { headers: authHeaders() });
      load();
      return true;
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Update failed.");
      return false;
    } finally { setBusy(false); }
  };

  const move = async (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    try {
      await axios.put(`${API}/admin/showcase-order`, { ids: next.map((i) => i.id) }, { headers: authHeaders() });
    } catch { toast.error("Could not save the order."); load(); }
  };

  const remove = async () => {
    if (!deleteId) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/showcase/${deleteId}`, { headers: authHeaders() });
      toast.success("Removed from the showcase.");
      setDeleteId(null);
      load();
    } catch { toast.error("Could not delete."); }
    finally { setBusy(false); }
  };

  const preview = items?.find((i) => i.id === previewId) || null;
  const inputCls = "w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2.5 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none";

  return (
    <div data-testid="showcase-manager" className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Film className="h-4.5 w-4.5 text-cyan-400" />
          <h2 className="font-display text-base font-bold text-[var(--pv-text)]">Homepage showcase — "The Standard"</h2>
          <span className="rounded-full border border-[var(--pv-border)] px-2 py-0.5 text-[10px] font-bold uppercase text-[var(--pv-text-4)]">Owner only</span>
        </div>
        <span data-testid="showcase-count" className="text-xs font-bold text-[var(--pv-text-4)]">{items ? items.length : "…"} / {max} videos</span>
      </div>
      <p className="mt-1.5 text-xs text-[var(--pv-text-4)]">
        These uploaded reference videos run in the public homepage marquee. Upload the files yourself — nothing is pulled from YouTube.
        Disabled videos stay here but leave the public reel.
      </p>

      <div className="mt-4 space-y-2.5">
        {!items ? (
          <p className="py-6 text-center text-sm text-[var(--pv-text-4)]"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></p>
        ) : items.length === 0 ? (
          <p data-testid="showcase-empty" className="rounded-2xl border border-dashed border-[var(--pv-border-strong)] py-8 text-center text-sm text-[var(--pv-text-4)]">
            No showcase videos yet — upload your first reference below.
          </p>
        ) : (
          items.map((it, idx) => (
            <div key={it.id} data-testid={`showcase-item-${it.id}`} className={`rounded-2xl border bg-[var(--pv-subtle)] p-3.5 ${it.enabled ? "border-[var(--pv-border)]" : "border-dashed border-[var(--pv-border)] opacity-60"}`}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="flex h-10 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-black/40 text-[9px] font-bold text-white/50">
                  {it.thumb_url ? <img src={`${API.replace(/\/api$/, "")}${it.thumb_url}`} alt="" className="h-full w-full object-cover" /> : <Play className="h-3.5 w-3.5" />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-[var(--pv-text)]">
                    <span className="mr-2 font-mono-label text-[10px] uppercase tracking-widest text-[var(--pv-text-4)]">#{idx + 1}</span>
                    {it.title || <span className="text-[var(--pv-text-4)]">Untitled</span>}
                  </p>
                  <p className="truncate text-[11px] text-[var(--pv-text-4)]">{it.creator ? `by ${it.creator} · ` : ""}{it.video_filename || "video"}{it.enabled ? "" : " · hidden from public reel"}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button data-testid={`showcase-up-${it.id}`} onClick={() => move(idx, -1)} disabled={idx === 0 || busy} className="rounded-full p-1.5 text-[var(--pv-text-4)] transition-colors hover:bg-[var(--pv-raised)] hover:text-[var(--pv-text)] disabled:opacity-30" aria-label="Move up"><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button data-testid={`showcase-down-${it.id}`} onClick={() => move(idx, 1)} disabled={idx === items.length - 1 || busy} className="rounded-full p-1.5 text-[var(--pv-text-4)] transition-colors hover:bg-[var(--pv-raised)] hover:text-[var(--pv-text)] disabled:opacity-30" aria-label="Move down"><ArrowDown className="h-3.5 w-3.5" /></button>
                  <button data-testid={`showcase-preview-${it.id}`} onClick={() => setPreviewId(it.id)} className="rounded-full px-3 py-1.5 text-[11px] font-bold text-cyan-300 transition-colors hover:bg-cyan-500/10">Preview</button>
                  <button data-testid={`showcase-edit-${it.id}`} onClick={() => { setEditId(editId === it.id ? null : it.id); setEditForm({ title: it.title, creator: it.creator, source: it.source || "" }); setFramePickFor(null); }} className="rounded-full px-3 py-1.5 text-[11px] font-bold text-[var(--pv-text-3)] transition-colors hover:bg-[var(--pv-raised)]">{editId === it.id ? "Close" : "Edit"}</button>
                  <button
                    data-testid={`showcase-toggle-${it.id}`}
                    onClick={() => patch(it.id, { enabled: !it.enabled })}
                    disabled={busy}
                    className={`rounded-full px-3 py-1.5 text-[11px] font-bold transition-colors ${it.enabled ? "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25" : "bg-[var(--pv-raised)] text-[var(--pv-text-4)] hover:text-[var(--pv-text-2)]"}`}
                  >
                    {it.enabled ? "Shown" : "Hidden"}
                  </button>
                  <button data-testid={`showcase-delete-${it.id}`} onClick={() => setDeleteId(it.id)} className="rounded-full p-1.5 text-rose-400 transition-colors hover:bg-rose-500/10" aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              {editId === it.id && (
                <div data-testid={`showcase-edit-form-${it.id}`} className="mt-3 border-t border-[var(--pv-border)] pt-3">
                  <div className="grid gap-2.5 sm:grid-cols-3">
                    <input data-testid={`showcase-edit-title-${it.id}`} value={editForm.title} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))} placeholder="Display title (optional)" maxLength={120} className={inputCls} />
                    <input data-testid={`showcase-edit-creator-${it.id}`} value={editForm.creator} onChange={(e) => setEditForm((f) => ({ ...f, creator: e.target.value }))} placeholder="Creator name (optional)" maxLength={120} className={inputCls} />
                    <input data-testid={`showcase-edit-source-${it.id}`} value={editForm.source} onChange={(e) => setEditForm((f) => ({ ...f, source: e.target.value }))} placeholder="Source / reference info (optional)" maxLength={200} className={inputCls} />
                  </div>
                  <div className="mt-2.5 flex flex-wrap items-center gap-2">
                    <label className="flex cursor-pointer items-center gap-1.5 rounded-full border border-[var(--pv-border)] px-3 py-1.5 text-[11px] font-bold text-[var(--pv-text-3)] transition-colors hover:border-cyan-500/30">
                      <RefreshCw className="h-3 w-3" /> Replace video
                      <input data-testid={`showcase-replace-video-${it.id}`} type="file" accept="video/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (f && (await patch(it.id, {}, { video: f }))) toast.success("Video replaced."); e.target.value = ""; }} />
                    </label>
                    <button
                      data-testid={`showcase-frame-pick-${it.id}`}
                      onClick={() => setFramePickFor(framePickFor === it.id ? null : it.id)}
                      className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-bold transition-colors ${framePickFor === it.id ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-300" : "border-[var(--pv-border)] text-[var(--pv-text-3)] hover:border-cyan-500/30"}`}
                    >
                      <Camera className="h-3 w-3" /> Choose frame from video
                    </button>
                    <label className="flex cursor-pointer items-center gap-1.5 rounded-full border border-[var(--pv-border)] px-3 py-1.5 text-[11px] font-bold text-[var(--pv-text-3)] transition-colors hover:border-cyan-500/30">
                      <ImageIcon className="h-3 w-3" /> {it.thumb_url ? "Upload custom thumbnail" : "Upload thumbnail"}
                      <input data-testid={`showcase-replace-thumb-${it.id}`} type="file" accept="image/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (f && (await patch(it.id, {}, { thumb: f }))) toast.success("Thumbnail updated."); e.target.value = ""; }} />
                    </label>
                    <span data-testid={`showcase-thumb-state-${it.id}`} className="text-[10px] font-semibold text-[var(--pv-text-4)]">
                      Thumbnail: {it.thumb_kind === "frame" ? `picked frame @ ${Math.floor((it.thumb_time || 0) / 60)}:${String(Math.floor((it.thumb_time || 0) % 60)).padStart(2, "0")}` : it.thumb_kind === "custom" ? "custom upload" : "first frame (default)"}
                    </span>
                    <button
                      data-testid={`showcase-edit-save-${it.id}`}
                      onClick={async () => { if (await patch(it.id, { title: editForm.title, creator: editForm.creator, source: editForm.source })) { toast.success("Saved."); setEditId(null); } }}
                      disabled={busy}
                      className="ml-auto rounded-full bg-cyan-500 px-4 py-1.5 text-[11px] font-bold text-white transition-colors hover:bg-cyan-600 disabled:opacity-60"
                    >
                      Save details
                    </button>
                  </div>
                  {framePickFor === it.id && (
                    <div className="mt-3">
                      <ThumbnailPicker
                        testid={`showcase-frame-picker-${it.id}`}
                        videoUrl={`${ORIGIN}${it.video_url}`}
                        busy={busy}
                        onCapture={async (file, time) => {
                          if (await patch(it.id, { thumb_time: time.toFixed(2) }, { thumb: file })) toast.success("Thumbnail set to the picked frame.");
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {items && items.length < max && !adding && (
        <button data-testid="showcase-add-open" onClick={() => setAdding(true)} className="mt-4 flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-cyan-600">
          <Plus className="h-3.5 w-3.5" /> Add showcase video
        </button>
      )}
      {adding && (
        <form data-testid="showcase-add-form" onSubmit={add} className="mt-4 grid gap-3 rounded-2xl border border-cyan-500/20 bg-[var(--pv-subtle)] p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block cursor-pointer rounded-2xl border border-dashed border-[var(--pv-border-strong)] px-4 py-3 text-xs font-semibold text-[var(--pv-text-3)] transition-colors hover:border-cyan-500/40">
              <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Video file (required)</span>
              {videoFile ? videoFile.name : "Choose video…"}
              <input data-testid="showcase-video-input" ref={videoRef} type="file" accept="video/*" className="hidden" onChange={(e) => setVideoFile(e.target.files?.[0] || null)} />
            </label>
            <label className="block cursor-pointer rounded-2xl border border-dashed border-[var(--pv-border-strong)] px-4 py-3 text-xs font-semibold text-[var(--pv-text-3)] transition-colors hover:border-cyan-500/40">
              <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Thumbnail (optional)</span>
              {thumbFile ? thumbFile.name : "Choose image…"}
              <input data-testid="showcase-thumb-input" ref={thumbRef} type="file" accept="image/*" className="hidden" onChange={(e) => setThumbFile(e.target.files?.[0] || null)} />
            </label>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <input data-testid="showcase-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Display title (optional)" maxLength={120} className={inputCls} />
            <input data-testid="showcase-creator-input" value={form.creator} onChange={(e) => setForm((f) => ({ ...f, creator: e.target.value }))} placeholder="Creator name (optional)" maxLength={120} className={inputCls} />
            <input data-testid="showcase-source-input" value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))} placeholder="Source / reference info (optional)" maxLength={200} className={inputCls} />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" data-testid="showcase-add-cancel" onClick={() => setAdding(false)} disabled={busy} className="rounded-full border border-[var(--pv-border)] px-4 py-2 text-xs font-bold text-[var(--pv-text-3)] transition-colors hover:bg-[var(--pv-raised)]">Cancel</button>
            <button type="submit" data-testid="showcase-add-btn" disabled={busy || !videoFile} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-5 py-2 text-xs font-bold text-white transition-colors hover:bg-cyan-600 disabled:opacity-60">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Upload &amp; add
            </button>
          </div>
        </form>
      )}

      {deleteId && (
        <div data-testid="showcase-delete-modal" className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={() => !busy && setDeleteId(null)}>
          <div className="w-full max-w-sm rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display text-base font-bold text-[var(--pv-text)]">Remove this showcase video?</h2>
            <p className="mt-2 text-xs text-[var(--pv-text-4)]">It's removed from the public homepage reel. This can't be undone.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button data-testid="showcase-delete-cancel" onClick={() => setDeleteId(null)} disabled={busy} className="rounded-full border border-[var(--pv-border)] px-4 py-2 text-xs font-bold text-[var(--pv-text-3)] hover:bg-[var(--pv-subtle)]">Cancel</button>
              <button data-testid="showcase-delete-confirm" onClick={remove} disabled={busy} className="flex items-center gap-1.5 rounded-full bg-rose-500 px-4 py-2 text-xs font-bold text-white hover:bg-rose-600 disabled:opacity-60">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {preview && <ShowcaseViewer item={preview} onClose={() => setPreviewId(null)} />}
    </div>
  );
};
